import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { UIUtils } from "../utils/uiUtils";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { getLoansPath } from "../utils/navigationUtils";
import { Resources } from "../utils/resources";
import BenefitsBuilder from "../components/BenefitsBuilder";
import Loader from "../components/Loader";
import { Button } from "../components/Button";
import ArticleList from "../components/ArticleList";
import Signposting from "../components/Signposting";
import MoratoriumTile from "../components/MoratoriumTile";
import PartnerOfferCard, { OFFER_BAND_LOQBOX } from "../components/PartnerOfferCard";
import GuarantorLoanTile from "../components/GuarantorLoanTile";
import { Popup } from "../components/Popup";
import ReviewActionsContent from "../components/ReviewActionsContent";
import { Intent } from "../utils/types/intent";
import affieTransparent from "../assets/affie-transparent.svg";
import creditIcon from "../assets/icons/credit-icon.svg";
import benefitsIcon from "../assets/icons/benefits-icon.svg";
import benefitsIllustration from "../assets/benefits-illustration.svg";
import discountsIcon from "../assets/icons/discounts-icon.svg";
import discountsWhiteIcon from "../assets/icons/discounts-white-icon.svg";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    userGoal,
    benefitsSummary,
    creditEligibility,
    isLoading,
    error,
    setDiscountsForGoal,
  } = useUser();
  const { setIsVisible } = useBottomNavBar();

  // Intent handling
  const [activeIntent] = useState<Intent | undefined>(
    (location.state as { activeIntent?: Intent })?.activeIntent,
  );

  // Refs for scrolling to highlighted sections
  const creditOptionsRef = useRef<HTMLDivElement>(null);
  const benefitsRef = useRef<HTMLDivElement>(null);
  const discountCardRef = useRef<HTMLDivElement>(null);
  const horizontalScrollRef = useRef<HTMLDivElement>(null);

  // State declarations
  const [isGoalExpanded, setIsGoalExpanded] = useState(false);
  const [needSignPosting, setNeedSignPosting] = useState(false);
  const [isReviewActionsPopupOpen, setIsReviewActionsPopupOpen] =
    useState(false);
  const [showFadeEffects, setShowFadeEffects] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, [setIsVisible]);

  useEffect(() => {
    if (userGoal) {
      setNeedSignPosting(
        userGoal.response.risk_score >= Resources.riskThreshold &&
          userGoal.response.risk_category != null,
      );
    }
  }, [userGoal]);

  // Scroll to highlighted section when intent is present
  useEffect(() => {
    if (activeIntent) {
      const scrollToSection = () => {
        let targetRef: React.RefObject<HTMLDivElement> | null = null;

        if (activeIntent === "ln" && creditOptionsRef.current) {
          targetRef = creditOptionsRef;
        } else if (activeIntent === "ben" && benefitsRef.current) {
          targetRef = benefitsRef;
        } else if (
          activeIntent === "ds" &&
          discountCardRef.current &&
          horizontalScrollRef.current
        ) {
          // Handle discounts intent - scroll to horizontal section first, then scroll horizontally
          const horizontalContainer = horizontalScrollRef.current;
          const discountCard = discountCardRef.current;

          // First scroll vertically to the horizontal scroll section
          const containerRect = horizontalContainer.getBoundingClientRect();
          const absoluteContainerTop = containerRect.top + window.pageYOffset;
          const verticalScrollTarget = absoluteContainerTop - 100; // Add some padding from top

          window.scrollTo({
            top: verticalScrollTarget,
            behavior: "smooth",
          });

          // Then scroll horizontally to show the discount card
          setTimeout(() => {
            const cardOffsetLeft = discountCard.offsetLeft;

            // Calculate scroll position to make card visible (not necessarily centered)
            const targetScrollLeft = cardOffsetLeft - 20; // 20px padding from left edge

            horizontalContainer.scrollTo({
              left: targetScrollLeft,
              behavior: "smooth",
            });
          }, 800); // Wait for vertical scroll to complete with slower timing

          return; // Exit early for discounts intent
        }

        if (targetRef?.current) {
          const element = targetRef.current;
          const elementRect = element.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const middle =
            absoluteElementTop -
            window.innerHeight / 2 +
            elementRect.height / 2;

          window.scrollTo({
            top: middle,
            behavior: "smooth",
          });
        }
      };

      // Delay scroll to ensure DOM is ready
      const timer = setTimeout(scrollToSection, 100);
      return () => clearTimeout(timer);
    }
  }, [activeIntent, needSignPosting]); // Include needSignPosting to re-trigger when layout changes

  // Different character limits for mobile vs desktop
  const getCharacterLimit = () => {
    return window.innerWidth >= 768 ? 120 : 60; // 120 chars on desktop, 60 on mobile
  };

  const [characterLimit, setCharacterLimit] = useState(getCharacterLimit());

  useEffect(() => {
    const handleResize = () => {
      setCharacterLimit(getCharacterLimit());
      checkScrollNeeded();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check if horizontal scrolling is needed and update fade effects visibility
  const checkScrollNeeded = () => {
    if (horizontalScrollRef.current) {
      const container = horizontalScrollRef.current;
      const needsScroll = container.scrollWidth > container.clientWidth;
      setShowFadeEffects(needsScroll);
    }
  };

  // Check scroll needed after component mounts and when content changes
  useEffect(() => {
    const timer = setTimeout(checkScrollNeeded, 100);
    return () => clearTimeout(timer);
  }, [userGoal, benefitsSummary]);

  const truncateText = (text: string, maxLength: number = 60) => {
    if (text.length <= maxLength) return text;
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(" ");
    return lastSpace > 0
      ? truncated.substring(0, lastSpace) + "..."
      : truncated + "...";
  };

  // Enhanced action handlers from Recommendations page
  const handleRetailDiscountsAction = () => {
    if (userGoal) {
      // Personalized: set category based on user's goal
      setDiscountsForGoal(
        userGoal.user_goal_id,
        userGoal.response.purchase_category,
      );
    }
    // Always navigate - Discounts page works with or without goal
    navigate("/discounts");
  };

  const handleEmmaTrialAction = () => {
    window.open("https://tidd.ly/4kY2bjW", "_blank");
  };

  const handleLoqboxAction = () => {
    window.open(
      "https://www.loqbox.com/en-gb/?affiliate_source=522418804&affiliate_ref=494797394&utm_source=gain&utm_medium=partner&utm_campaign=afforda_suggestions",
      "_blank",
    );
  };

  // Keyboard navigation for horizontal scroll
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!horizontalScrollRef.current) return;

    const scrollAmount = 200;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      horizontalScrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      horizontalScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }

  if (isLoading) {
    return <Loader message="Loading your dashboard..." />;
  }

  if (error) {
    UIUtils.showMessageOnSnackBar(
      "An error occurred while fetching user data.",
    );
    navigate("/");
    return null;
  }

  const firstName = user?.first_name || "";

  // The credit tile is replaced by signposting (high-risk goal) or the
  // moratorium tile. The guarantor loan tile is shown whenever the
  // "Other credit options" tile is shown.
  const showSignposting = Boolean(
    needSignPosting && userGoal?.response.risk_category,
  );
  const showCreditOptions =
    !showSignposting && creditEligibility?.show_credit_tile !== false;

  // Helper function to determine if a section should be highlighted
  const shouldHighlightSection = (sectionType: string) => {
    if (!activeIntent) return false;

    if (activeIntent === "ln" && sectionType === "credit") return true;
    if (activeIntent === "ben" && sectionType === "benefits") return true;
    if (activeIntent === "ds" && sectionType === "discounts") return true;

    return false;
  };

  return (
    <>
      {/* Regular content - responsive container */}
      <div className="container-medium">
        <div className="rounded-20 pb-[20px] bg-primary mb-6 text-center overflow-hidden">
          <div className="relative">
            <div className="text-center">
              <h1 className="text-3xl mb-2 pt-[20px] px-[20px]">
                Hello{firstName ? `, ${firstName}` : ''}!
              </h1>
              {userGoal && (
                <div>
                  <p className="px-[20px] md:max-w-lg lg:max-w-3xl md:mx-auto">
                    <span className="font-bold">You told us:</span>{" "}
                    {isGoalExpanded
                      ? userGoal.goal_statement
                      : truncateText(userGoal.goal_statement, characterLimit)}
                  </p>
                  {userGoal.goal_statement.length > characterLimit && (
                    <button
                      onClick={() => setIsGoalExpanded(!isGoalExpanded)}
                      className=" underline text-xs mt-1 font-bold"
                    >
                      {isGoalExpanded ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div
              className={`absolute top-0 right-0 md:block hidden ${
                !isGoalExpanded &&
                userGoal?.goal_statement &&
                userGoal.goal_statement.length <= characterLimit
                  ? "scale-75 origin-top-right"
                  : ""
              }`}
            >
              <img src={affieTransparent} alt="" />
            </div>
          </div>
        </div>

        {showCreditOptions && <GuarantorLoanTile />}

        <div className="md:flex md:gap-5">
          {showSignposting && userGoal?.response.risk_category ? (
            <div className="mb-6 md:flex-1">
              <Signposting
                category={userGoal.response.risk_category}
                onWhyAmISeeingThis={() => setIsReviewActionsPopupOpen(true)}
              />
            </div>
          ) : creditEligibility?.show_credit_tile === false ? (
            <MoratoriumTile
              innerRef={creditOptionsRef}
              isHighlighted={shouldHighlightSection("credit")}
            />
          ) : (
            <div
              ref={creditOptionsRef}
              className={`mb-6 md:flex-1 ${
                shouldHighlightSection("credit")
                  ? "border-4 border-primary rounded-20 p-2"
                  : ""
              }`}
            >
              <div className="flex mb-3">
                <img alt="credit icon" src={creditIcon} />
                <h2 className="text-2xl ml-2.5">OTHER CREDIT OPTIONS</h2>
              </div>
              <div className="container-white-rounded">
                <p className="mt-2">
                  You may be able to access loans and other credit products
                  through our third-party lenders, subject to approval.
                </p>
                <hr className="my-4 border-black/10" />
                <div className="flex justify-center">
                  <Button
                    text="Apply for a loan"
                    onClick={() => navigate(getLoansPath())}
                  />
                </div>
                <p className="text-center font-bold mt-2">
                  Representative 1333%APR
                </p>
                <p className="text-center">
                  Warning: Late repayments can cause you serious money
                  problems. For help go to{" "}
                  <a
                    href="https://www.moneyhelper.org.uk"
                    className="underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    moneyhelper.org.uk
                  </a>
                </p>
              </div>
            </div>
          )}

          <div
            ref={benefitsRef}
            className={`mb-6 md:flex-1 ${
              shouldHighlightSection("benefits")
                ? "border-4 border-primary rounded-20 p-2"
                : ""
            }`}
          >
            <div className="flex mb-3">
              <img alt="benefits icon" src={benefitsIcon} />
              <h2 className="text-2xl ml-2.5">BENEFITS</h2>
            </div>
            <div className="container-white-rounded relative overflow-hidden">
              <img
                alt=""
                aria-hidden="true"
                src={benefitsIllustration}
                className="absolute -top-3 -right-9 w-28 pointer-events-none"
              />
              <div className="relative">
                <BenefitsBuilder
                  benefitsSummary={benefitsSummary}
                  useButtons={true}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex mb-3">
          <img alt="discounts icon" src={discountsIcon} />
          <h2 className="text-2xl ml-2.5">OFFERS</h2>
        </div>
      </div>

      {/* Horizontal scroll section - match container-medium width on desktop */}
      <div className="w-full mb-6 flex justify-center">
        <div className="w-full max-w-[1130px] relative">
          {/* Left fade shadow */}
          <div
            className={`absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-gray-50/50 to-transparent z-10 pointer-events-none opacity-0 transition-opacity duration-300 ${
              showFadeEffects ? "block" : "hidden"
            }`}
            id="left-blur"
          ></div>

          {/* Right fade shadow */}
          <div
            className={`absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-gray-50/50 to-transparent z-10 pointer-events-none transition-opacity duration-300 ${
              showFadeEffects ? "opacity-100 block" : "hidden"
            }`}
            id="right-blur"
          ></div>

          <div
            ref={horizontalScrollRef}
            className="overflow-x-scroll overflow-y-hidden scrollbar-auto-hide"
            style={{
              touchAction: "pan-x pinch-zoom",
              overscrollBehaviorX: "contain",
            }}
            role="region"
            aria-label="Offers carousel"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onScroll={(e) => {
              if (!showFadeEffects) return;

              const target = e.target as HTMLElement;
              const leftBlur = document.getElementById("left-blur");
              const rightBlur = document.getElementById("right-blur");

              if (leftBlur && rightBlur) {
                // Show left blur if scrolled right
                leftBlur.style.opacity = target.scrollLeft > 0 ? "1" : "0";

                // Show right blur if not scrolled to the end
                const isAtEnd =
                  target.scrollLeft >=
                  target.scrollWidth - target.clientWidth - 1;
                rightBlur.style.opacity = isAtEnd ? "0" : "1";
              }
            }}
          >
            <div className="flex gap-4 md:gap-5 pb-4 items-stretch">
              <PartnerOfferCard
                header={
                  <img
                    src="/loqbox_lockup.png"
                    alt="Loqbox logo"
                    className="h-[62px] w-auto"
                  />
                }
                bandClassName={OFFER_BAND_LOQBOX}
                onClick={handleLoqboxAction}
                dataTrack="Loqbox regret"
              >
                Loqbox could help you build credit, boosting your approval
                chances in future.
              </PartnerOfferCard>

              <PartnerOfferCard
                header={
                  <img
                    src="/emma-logo.png"
                    alt="Emma logo"
                    className="h-[120px] w-auto max-w-none"
                  />
                }
                onClick={handleEmmaTrialAction}
                dataTrack="Emma Regret"
              >
                Try the Emma app to help track spending and budget.
              </PartnerOfferCard>

              <PartnerOfferCard
                innerRef={discountCardRef}
                header={
                  <img src={discountsWhiteIcon} alt="" className="w-8 h-8" />
                }
                onClick={handleRetailDiscountsAction}
                dataTrack="Discounts Dashboard"
                isHighlighted={shouldHighlightSection("discounts")}
              >
                Don’t pay full price for what matters. Check your discounts
                before you buy.
              </PartnerOfferCard>
            </div>
          </div>
        </div>
      </div>

      {/* Guides section - separate container to maintain proper width */}
      <div className="container-medium">
        <div className="mb-8">
          {/* Only render ArticleList once userGoal is fully loaded */}
          {userGoal?.goal_statement && (
            <div className="mt-3">
              <ArticleList userGoalStatement={userGoal.goal_statement} />
            </div>
          )}
        </div>
      </div>

      {/* Review Actions Popup */}
      <Popup
        isOpen={isReviewActionsPopupOpen}
        onClose={() => setIsReviewActionsPopupOpen(false)}
        title="Why we suggested these actions"
      >
        <ReviewActionsContent
          onClose={() => setIsReviewActionsPopupOpen(false)}
        />
      </Popup>
    </>
  );
};

export default Dashboard;
